import React, { ErrorInfo } from "react";

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  onReset: () => void;
  autoResetTimeMs?: number;
  maxResetAttempts?: number;
  fallbackComponent?: () => React.ReactNode;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  resetTimeoutId: NodeJS.Timeout | null;
  resetCount: number;
}

const DefaultFallbackComponent = () => null

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      resetTimeoutId: null,
      resetCount: 0
    };
    this.resetErrorBoundary = this.resetErrorBoundary.bind(this);
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    
    const { autoResetTimeMs = 1000, maxResetAttempts = 3 } = this.props;
    
    this.setState({
      error,
      errorInfo,
      resetCount: this.state.resetCount + 1
    });

    // Auto-reset after specified time if we haven't exceeded max attempts
    if (this.state.resetCount < maxResetAttempts) {
      const resetTimeoutId = setTimeout(() => {
        this.resetErrorBoundary();
      }, autoResetTimeMs);
      
      this.setState({ resetTimeoutId });
    }
  }

  resetErrorBoundary() {
    // Clear any existing timeout
    if (this.state.resetTimeoutId) {
      clearTimeout(this.state.resetTimeoutId);
    }
    
    // Reset the error boundary state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      resetTimeoutId: null
    });
    
    // Call optional onReset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  }

  componentWillUnmount() {
    // Clean up timeout on unmount
    if (this.state.resetTimeoutId) {
      clearTimeout(this.state.resetTimeoutId);
    }
  }

  render() {
    if (this.state.hasError) {
      const { 
        fallbackComponent: FallbackComponent = DefaultFallbackComponent
      } = this.props;
      return (<FallbackComponent />);
    }

    return this.props.children;
  }
}